const hexToRgb = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Deterministic per-pixel noise in [0,1) so overlapping stamps never build up.
const grain = (x, y) => {
  let h = (x * 374761393 + y * 668265263) ^ 0x5bd1e995;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

// Pixel-stamped stroke that never paints over line-art walls. hex=null restores original pixels (eraser).
export function createStroke(ctx, mask, hex, radius, original) {
  const { width: w, height: h } = ctx.canvas;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const base = hex ? new Uint8ClampedArray(d) : null;
  const cov = hex ? new Float32Array(w * h) : null;
  const rgb = hex ? hexToRgb(hex) : null;
  const r = Math.max(1, Math.round(radius));
  const r2 = r * r;
  let last = null;

  const stamp = (cx, cy) => {
    const x0 = Math.max(0, cx - r), x1 = Math.min(w - 1, cx + r);
    const y0 = Math.max(0, cy - r), y1 = Math.min(h - 1, cy + r);
    if (x1 < x0 || y1 < y0) return;
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = x - cx, dy = y - cy;
        const dist2 = dx * dx + dy * dy;
        if (dist2 > r2) continue;
        const p = y * w + x;
        if (mask[p]) continue;
        const i = p * 4;
        if (!rgb) {
          d[i] = original[i]; d[i + 1] = original[i + 1]; d[i + 2] = original[i + 2]; d[i + 3] = 255;
          continue;
        }
        const g = grain(x, y);
        let a = g < 0.07 ? 0.3 : 0.72 + 0.28 * g;
        if (dist2 > r2 * 0.7) a *= 0.75;
        if (a <= cov[p]) continue;
        cov[p] = a;
        const shade = 1 - (g - 0.5) * 0.14;
        d[i] = base[i] + (rgb[0] * shade - base[i]) * a;
        d[i + 1] = base[i + 1] + (rgb[1] * shade - base[i + 1]) * a;
        d[i + 2] = base[i + 2] + (rgb[2] * shade - base[i + 2]) * a;
        d[i + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0, x0, y0, x1 - x0 + 1, y1 - y0 + 1);
  };

  const to = (fx, fy) => {
    const x = Math.round(fx), y = Math.round(fy);
    if (!last) { stamp(x, y); last = [x, y]; return; }
    const [lx, ly] = last;
    const steps = Math.max(1, Math.ceil(Math.hypot(x - lx, y - ly) / Math.max(1, r / 2)));
    for (let s = 1; s <= steps; s++) stamp(Math.round(lx + ((x - lx) * s) / steps), Math.round(ly + ((y - ly) * s) / steps));
    last = [x, y];
  };

  return { to };
}
